/**
 * Object field template (FEAT-2026-0005, Phase 2 polish).
 *
 * The biggest readability win in the form. Behaviour by depth:
 *
 *  • Root level (no idSchema parent) — render each top-level property as a
 *    collapsible Accordion section. The user can expand the section they're
 *    editing and collapse the rest, instead of scrolling through hundreds
 *    of fields stacked vertically.
 *
 *  • Nested objects (depth ≥ 1) — render as a compact 2-column grid of
 *    label-on-top fields when every property is a primitive (e.g. a `page`
 *    object with top/right/bottom/left, or a zoneLabel with x/y/label).
 *    Fall back to a stacked layout for mixed-shape objects.
 *
 * The result: page margins go from 4 stacked giant inputs (~280 px) to a
 * 2×2 grid of small inputs (~80 px), and the whole config fits on one
 * screen with strategic accordion expansion.
 */

import { useEffect, useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Box, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import type { ObjectFieldTemplateProps } from '@rjsf/utils';
import { useReportTemplateStore } from '../useReportTemplateStore';
import { useFormSearch, matchesQuery } from './FormSearchContext';

const ROOT_ID = 'root';

/** Friendly section titles for the root-level accordion headings.
 *  Falls back to humanising the raw schema key for anything not listed.
 *  Covers Atterberg + PSD; new templates extend by adding entries here. */
const SECTION_TITLES: Record<string, string> = {
  schemaVersion:    'Schema version',
  page:             'Page margins',
  // Atterberg
  trialTables:      'Trial tables (LL / PL)',
  resultsBlock:     'Results block (LL / PL / PI · USCS)',
  flowCurve:        'Flow Curve chart',
  plasticityChart:  'Plasticity Chart',
  // PSD
  gradationSummary: 'Gradation Summary',
  resultsTable:     'Results table (Size / Passing)',
  labComments:      'Laboratory comments',
  prepUscs:         'Preparation method · USCS description',
  gradationCurve:   'Gradation Curve chart',
  // Shared Header & Footer
  header:           'Header (logo, title, project & sample tables)',
  footer:           'Footer (lab address, page number, signoffs)',
};

const SECTION_BLURBS: Record<string, string> = {
  page:             'Where the page content starts on each side, in points.',
  // Atterberg
  trialTables:      'Heading and row geometry for the LL and PL trial tables.',
  resultsBlock:     'Heading + row geometry for the info-tables to the right of the Flow Curve.',
  flowCurve:        'Slot dimensions, chart styling, axis ticks, marker style, and the (25-blow / LL) indicator.',
  plasticityChart:  'Slot dimensions, plot border, line endpoints, and the 5 zone-label coordinates.',
  // PSD
  gradationSummary: 'Heading + 9 fixed rows (gravel/sand/fines/D60/D30/D10/Cc/Cu) on the upper-left.',
  resultsTable:     'Heading + per-sieve passing values, two-column on the upper-right.',
  labComments:      'Heading + comments box (border colour, text colour).',
  prepUscs:         'Two-column row under Lab Comments — preparation method and USCS description.',
  gradationCurve:   'Chart slot dims, axes (log scale, ticks, fonts), series + sieve plotline styling.',
  // Shared Header & Footer (apply across every test)
  header:           'Logo size, title fonts, divider, project / sample info table geometry. Applies to every report.',
  footer:           'Lab address, page number, signoff lines, bottom note. Applies to every report.',
};

const humanise = (key: string): string =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

/**
 * Build a single lower-cased haystack string that the search query is matched
 * against. Includes the section's machine name, friendly title, blurb, and
 * every nested field name + description — flattened to one line so a single
 * substring test can decide whether to show the section.
 */
function collectSearchableText(
  name: string,
  title: string,
  blurb: string | undefined,
  subSchema: any,
): string {
  const parts: string[] = [name, title, blurb ?? '', humanise(name)];
  const visit = (sch: any): void => {
    if (!sch || typeof sch !== 'object') return;
    if (sch.title) parts.push(String(sch.title));
    if (sch.description) parts.push(String(sch.description));
    if (sch.properties && typeof sch.properties === 'object') {
      for (const [k, v] of Object.entries(sch.properties)) {
        parts.push(k, humanise(k));
        visit(v);
      }
    }
    if (Array.isArray(sch.prefixItems)) sch.prefixItems.forEach(visit);
    if (sch.items) visit(sch.items);
  };
  visit(subSchema);
  return parts.join(' ').toLowerCase();
}

export const ObjectFieldTemplate = (props: ObjectFieldTemplateProps) => {
  const { idSchema, properties, title, description, schema } = props;
  const isRoot = idSchema?.$id === ROOT_ID;

  // ── ROOT — accordion sections per top-level property. ────────────────────
  if (isRoot) {
    return (
      <Box>
        {properties.map((p) => {
          // Hide the schemaVersion field at root — it's an internal marker.
          if (p.name === 'schemaVersion') {
            return <div key={p.name} style={{ display: 'none' }}>{p.content}</div>;
          }
          // Pull the per-property sub-schema so RootSection can use field
          // names + descriptions for search matching.
          const sub = (schema.properties as any)?.[p.name];
          return (
            <RootSection key={p.name} name={p.name} subSchema={sub}>
              {p.content}
            </RootSection>
          );
        })}
      </Box>
    );
  }

  // ── Nested object — try a compact grid layout. ───────────────────────────
  // If every property is a primitive (string/number/boolean/integer), render
  // them in a 2-column grid. Otherwise stack normally.
  const allPrimitive = properties.every((p) => {
    const sub = (schema.properties as any)?.[p.name];
    const t = sub?.type;
    return t === 'string' || t === 'number' || t === 'integer' || t === 'boolean';
  });

  // Phase 8 form-density polish: visually tag every nested-object group
  // so the user can SEE where one sub-config ends and the next begins.
  // The Atterberg flowCurve config has 18 primitive fields plus
  // chartMargin (tuple), spacing (tuple), and displayPos (object) —
  // without this distinction the whole thing reads as one
  // undifferentiated wall of inputs.
  return (
    <Box
      sx={{
        mb: 1,
        // Left-border accent ONLY (no fill) — the grey wash made the
        // form feel busy. Border + uppercase title is enough to read
        // as "logical group".
        pl: 1,
        py: 0.5,
        borderLeft: '2px solid',
        borderColor: 'divider',
      }}
    >
      {title && (
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: '0.78rem',
            fontWeight: 600,
            mb: 0.5,
            color: 'text.primary',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, lineHeight: 1.3 }}>
          {description}
        </Typography>
      )}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: allPrimitive ? 'repeat(2, minmax(0, 1fr))' : '1fr',
        columnGap: 1.5,
        rowGap: 0,
        // Stretch booleans across full width — they're switches, not inputs.
        '& > [data-rjsf-bool="true"]': { gridColumn: '1 / -1' },
      }}>
        {properties.map((p) => (
          <Box key={p.name}>{p.content}</Box>
        ))}
      </Box>
    </Box>
  );
};

// ── Root accordion section ──────────────────────────────────────────────────

interface RootSectionProps {
  name: string;
  /** Sub-schema for this section. Used to derive nested field names +
   *  descriptions for search matching. */
  subSchema?: any;
  children: React.ReactNode;
}

const RootSection = ({ name, subSchema, children }: RootSectionProps) => {
  // Default-expand the most-edited sections; collapse the rest. Once the user
  // toggles, their preference is remembered for the session via local state.
  // Header / footer expand by default for the cross-template Shared template
  // so first-time users land on useful fields without an extra click.
  const [expanded, setExpanded] = useState(
    name === 'flowCurve' || name === 'plasticityChart' || name === 'page'
    || name === 'gradationCurve' || name === 'gradationSummary'
    || name === 'header' || name === 'footer',
  );
  const setActiveSection = useReportTemplateStore((s) => s.setActiveSection);
  const activeSection    = useReportTemplateStore((s) => s.activeSection);
  const isActive         = activeSection === name;

  const title = SECTION_TITLES[name] ?? humanise(name);
  const blurb = SECTION_BLURBS[name];

  // ── Search-driven visibility ─────────────────────────────────────────────
  // The form-pane search box writes a lower-cased query into context. A
  // section is shown when:
  //   • the query is empty (always show), OR
  //   • the section name / title / blurb contains the query, OR
  //   • any nested field name OR description contains the query.
  // When a section matches because of a nested field, we auto-expand it so
  // the user lands on the matching control instead of staring at a closed
  // accordion.
  const { query } = useFormSearch();
  const haystack = collectSearchableText(name, title, blurb, subSchema);
  const matches  = matchesQuery(haystack, query);

  // Auto-expand on a non-empty query that DOES match (the user wants to see
  // the control immediately) and auto-restore default state when the query
  // is cleared. We track the last-seen query so toggling the accordion
  // manually mid-search isn't fought against by re-render.
  useEffect(() => {
    if (query && matches) setExpanded(true);
  }, [query, matches]);

  if (query && !matches) return null;

  return (
    <Accordion
      disableGutters
      expanded={expanded}
      onChange={(_e, v) => {
        setExpanded(v);
        // Setting the active section drives the highlight overlay on the
        // live preview pane. Expanding "selects" this section; collapsing
        // de-selects ONLY when this was the active one (so collapsing a
        // non-active section doesn't accidentally clear someone else's
        // highlight).
        if (v) setActiveSection(name);
        else if (isActive) setActiveSection(null);
      }}
      sx={{
        '&:before': { display: 'none' },
        boxShadow: 'none',
        border: 1, borderColor: isActive ? 'primary.main' : 'divider',
        borderRadius: 1,
        mb: 1,
        transition: 'border-color 200ms ease',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore fontSize="small" />}
        // Clicking an already-expanded section's header re-asserts the
        // active section (handy if the user expanded several earlier and
        // wants to jump the highlight back to this one without collapsing).
        onClick={() => { if (expanded) setActiveSection(name); }}
        sx={{
          minHeight: 36, px: 1.5,
          '& .MuiAccordionSummary-content': { my: 0.5 },
          backgroundColor: isActive ? (theme) => theme.palette.action.selected : undefined,
          transition: 'background-color 200ms ease',
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
            {title}
          </Typography>
          {blurb && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', lineHeight: 1.2 }}>
              {blurb}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1.5, pt: 0.5 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};
